import { FunctionComponent, useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './FeedingSpecificMoments.module.scss';
import { Option } from '@spike/model';
import { useMarketplace, useMasterData, useTimeZone } from 'hooks';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import clsx from 'clsx';
import DateSelectorComponent from 'components/CreateBooking/DateSelector/DateSelectorSection/DateSelectorComponent';
import {
    getTimeOptionsForMoment,
    getDefaultTimeForMoment,
    ConfiguredFeedingTimes
} from './feedingSpecificMomentsUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';

export interface MomentTimeMap {
    [momentId: number]: string;
}

/** Sobre el Drawer (z-index 10002), si no el listado queda oculto detrás */
const HOURS_DROPDOWN_Z = 10010;

interface FeedingSpecificMomentsProps {
    selectedMoments: Array<number>;
    momentTimes: MomentTimeMap;
    onMomentsChange: (moments: Array<number>) => void;
    onMomentTimesChange: (momentTimes: MomentTimeMap) => void;
    disabled?: Array<number>;
    editing?: boolean;
}

const FeedingSpecificMoments: FunctionComponent<FeedingSpecificMomentsProps> = props => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const timeZone = useTimeZone();
    const feedingMomentTypes = masterData.feedingMomentTypes;
    const [openDropdownMomentId, setOpenDropdownMomentId] = useState<number | null>(null);
    const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
    const triggerRefs = useRef<Record<number, HTMLDivElement | null>>({});

    useEffect(() => {
        if (openDropdownMomentId != null && triggerRefs.current[openDropdownMomentId]) {
            setDropdownRect(triggerRefs.current[openDropdownMomentId]!.getBoundingClientRect());
        } else {
            setDropdownRect(null);
        }
    }, [openDropdownMomentId]);

    const configuredTimes: ConfiguredFeedingTimes = useMemo(() => {
        return getFeedingSlotTimes(marketplace, masterData);
    }, [marketplace.settings, masterData.feedingMomentTypes]);

    const handleMomentChange = (momentId: number, checked: boolean) => {
        if (props.disabled?.includes(momentId) && !props.selectedMoments.includes(momentId)) {
            return;
        }

        let newMoments: Array<number>;

        if (props.editing) {
            if (checked) {
                newMoments = [momentId];
            } else {
                newMoments = [];
            }
        } else {
            if (checked) {
                newMoments = [...props.selectedMoments, momentId];
            } else {
                newMoments = props.selectedMoments.filter(m => m !== momentId);
                const { [momentId]: _removed, ...restTimes } = props.momentTimes;
                props.onMomentTimesChange(restTimes);
            }
        }

        if (checked && !props.momentTimes[momentId]) {
            props.onMomentTimesChange({
                ...props.momentTimes,
                [momentId]: getDefaultTimeForMoment(momentId, configuredTimes, timeZone)
            });
        }
        props.onMomentsChange(newMoments);
    };

    const getDisplayTime = (momentId: number): string => {
        return props.momentTimes[momentId] || getDefaultTimeForMoment(momentId, configuredTimes, timeZone);
    };

    return (
        <div className={styles.container}>
            {feedingMomentTypes.map((moment: Option<number>, index: number) => {
                const momentId = moment.id;
                const isSelected = props.selectedMoments.includes(momentId);
                const isDisabled = props.disabled?.includes(momentId) && !props.selectedMoments.includes(momentId);
                const isDropdownOpen = openDropdownMomentId === momentId;
                const displayTime = getDisplayTime(momentId);

                return (
                    <div
                        key={moment.id}
                        className={clsx(styles.momentItem, index > 0 && styles.momentItemSeparator)}
                    >
                        <div className={styles.momentRow}>
                            <Checkbox
                                label={moment.name}
                                checked={isSelected}
                                onChange={(checked: boolean) => handleMomentChange(moment.id, checked)}
                                className={isDisabled ? styles.disabled : undefined}
                            />
                            <div className={clsx(styles.timeBlock, isDisabled && styles.disabled)}>
                                <span className={styles.timeLabel}>{displayTime}</span>
                                <div
                                    ref={el => {
                                        triggerRefs.current[momentId] = el;
                                    }}
                                    className={clsx(styles.dropdownTriggerWrap, !isSelected && styles.dropdownDisabled)}
                                >
                                    <DateSelectorComponent
                                        id={`feeding-hour-${momentId}`}
                                        expanded={isDropdownOpen}
                                        onExpand={() => {
                                            if (!isSelected) return;
                                            setOpenDropdownMomentId(prev => (prev === momentId ? null : momentId));
                                        }}
                                        onClickAway={() => setOpenDropdownMomentId(null)}
                                    >
                                        {null}
                                    </DateSelectorComponent>
                                </div>
                                {isDropdownOpen &&
                                    dropdownRect &&
                                    createPortal(
                                        <div
                                            className={styles.hoursDropdownContent}
                                            style={{
                                                position: 'fixed',
                                                top: dropdownRect.bottom + 4,
                                                left: dropdownRect.left,
                                                zIndex: HOURS_DROPDOWN_Z
                                            }}
                                            onMouseDown={e => e.stopPropagation()}
                                        >
                                            <div className={styles.hoursDropdownList}>
                                                {getTimeOptionsForMoment(momentId, configuredTimes, timeZone).map(
                                                    opt => (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            className={clsx(styles.hoursDropdownItem, {
                                                                [styles.hoursDropdownItemSelected]:
                                                                    displayTime === opt.value
                                                            })}
                                                            onClick={() => {
                                                                if (!isSelected) return;
                                                                props.onMomentTimesChange({
                                                                    ...props.momentTimes,
                                                                    [momentId]: opt.value
                                                                });
                                                                setOpenDropdownMomentId(null);
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FeedingSpecificMoments;
